import { Patient, HealthMetrics } from '../types/patient';
import {
  defaultWellbeingFactors,
  generateDefaultAppleHealthData,
  defaultHealthPlan
} from '../data/defaultPatientData';
import { DashboardData } from './parseXmlToJson';

/**
 * Generate a unique ID for the patient
 */
function generatePatientId(): string {
  return `uploaded-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Helper function to find lab value by name
 */
function findLabValue(labResults: any[], labName: string): number | undefined {
  const lab = labResults.find(l => l.name === labName);
  return lab ? lab.value : undefined;
}

/**
 * Transform parsed dashboard data to Patient interface
 *
 * The parser now outputs dashboard-ready data, so this is mostly a direct mapping
 * with the addition of default values for wellbeing, wearables, and plans.
 */
export function transformEhrToPatient(data: DashboardData): Patient {
  const { labResults, healthMetrics: legacyMetrics } = data;

  // Extract values from lab results for new structure
  const glucose = findLabValue(labResults, 'Glucose');
  const a1c = findLabValue(labResults, 'Hemoglobin A1c');
  const insulinLevel = findLabValue(labResults, 'Fasting Insulin');
  const creatinine = findLabValue(labResults, 'Creatinine');
  const egfr = findLabValue(labResults, 'eGFR');
  const apoB = findLabValue(labResults, 'Apolipoprotein B');
  const ldl = findLabValue(labResults, 'LDL Cholesterol');
  const hdl = findLabValue(labResults, 'HDL Cholesterol');
  const totalCholesterol = findLabValue(labResults, 'Total Cholesterol');

  // Calculate risk for metabolic analysis (Updated with Cleveland HeartLab thresholds)
  const metabolicRisk = (): 'low' | 'medium' | 'high' => {
    // Cleveland HeartLab ranges:
    // Glucose: 65-99 optimal, 100-125 moderate/prediabetes, ≥126 high/diabetes
    // HbA1c: <5.7 optimal, 5.7-6.4 moderate/prediabetes, >6.4 high/diabetes
    if (glucose && glucose >= 126) return 'high';
    if (a1c && a1c > 6.4) return 'high';
    if (glucose && glucose >= 100) return 'medium';
    if (a1c && a1c >= 5.7) return 'medium';
    return 'low';
  };

  // Calculate risk for cholesterol analysis (Updated with Cleveland HeartLab thresholds)
  const cholesterolRisk = (): 'low' | 'medium' | 'high' => {
    // Cleveland HeartLab ranges:
    // LDL: <100 optimal, 100-129 moderate, ≥130 high
    // ApoB: <90 optimal, 90-129 moderate, ≥130 high
    // HDL: ≥40 optimal, <40 high risk (also <50 is borderline)
    // Triglycerides: <150 optimal, 150-199 moderate, ≥200 high
    if (ldl && ldl >= 130) return 'high';
    if (apoB && apoB >= 130) return 'high';
    if (hdl && hdl < 40) return 'high';
    if (totalCholesterol && totalCholesterol >= 200) return 'high';

    if (ldl && ldl >= 100) return 'medium';
    if (apoB && apoB >= 90) return 'medium';
    if (hdl && hdl < 50) return 'medium';

    return 'low';
  };

  // Map health metrics to new structure
  const healthMetrics: HealthMetrics = {
    // New structure
    obesityAnalysis: {
      bmi: legacyMetrics.obesity?.bmi || 24,
      vo2Max: 35 + Math.random() * 10, // Mock data
      bodyFatPercentage: 18 + Math.random() * 8, // Mock data
      muscleMassIndex: 30 + Math.random() * 10, // Mock data
      risk: legacyMetrics.obesity?.risk as 'low' | 'medium' | 'high' || 'low'
    },
    metabolicAnalysis: {
      insulinLevel,
      a1c,
      glucose,
      creatinine,
      egfr,
      risk: metabolicRisk()
    },
    cholesterolAnalysis: {
      apoB,
      ldl: ldl || legacyMetrics.heartDisease?.cholesterol?.ldl || 100,
      hdl: hdl || legacyMetrics.heartDisease?.cholesterol?.hdl || 50,
      total: totalCholesterol || legacyMetrics.heartDisease?.cholesterol?.total || 180,
      risk: cholesterolRisk()
    },
    cardiovascularAnalysis: {
      systolic: legacyMetrics.hypertension?.systolic || 120,
      diastolic: legacyMetrics.hypertension?.diastolic || 80,
      heartRate: 70 + Math.floor(Math.random() * 20), // Will be extracted from vitals if available
      respiratoryRate: 14 + Math.floor(Math.random() * 4), // Will be extracted from vitals if available
      risk: legacyMetrics.hypertension?.risk as 'low' | 'medium' | 'high' || 'low'
    },
    // Legacy fields for backwards compatibility
    metabolicSyndrome: legacyMetrics.metabolicSyndrome ? {
      risk: legacyMetrics.metabolicSyndrome.risk as 'low' | 'medium' | 'high',
      score: legacyMetrics.metabolicSyndrome.score,
      factors: legacyMetrics.metabolicSyndrome.factors
    } : undefined,
    hypertension: legacyMetrics.hypertension ? {
      systolic: legacyMetrics.hypertension.systolic,
      diastolic: legacyMetrics.hypertension.diastolic,
      risk: legacyMetrics.hypertension.risk as 'low' | 'medium' | 'high'
    } : undefined,
    liverEnzymes: legacyMetrics.liverEnzymes ? {
      alt: legacyMetrics.liverEnzymes.alt,
      ast: legacyMetrics.liverEnzymes.ast,
      risk: legacyMetrics.liverEnzymes.risk as 'low' | 'medium' | 'high'
    } : undefined,
    obesity: legacyMetrics.obesity ? {
      bmi: legacyMetrics.obesity.bmi,
      risk: legacyMetrics.obesity.risk as 'low' | 'medium' | 'high'
    } : undefined,
    heartDisease: legacyMetrics.heartDisease ? {
      cholesterol: {
        total: legacyMetrics.heartDisease.cholesterol.total,
        ldl: legacyMetrics.heartDisease.cholesterol.ldl,
        hdl: legacyMetrics.heartDisease.cholesterol.hdl
      },
      risk: legacyMetrics.heartDisease.risk as 'low' | 'medium' | 'high'
    } : undefined
  };

  // Transform medications from simple format to complex Medication type
  const medications = data.medications.map((med) => ({
    id: med.id,
    name: med.name,
    dosage: med.dosage,
    code: med.code,
    datePairs: [{
      prescribedDate: med.prescribedDate,
      discontinuedDate: med.status === 'discontinued' ? med.prescribedDate : undefined,
      status: med.status
    }],
    overallStatus: med.status,
    totalPrescriptions: 1,
    totalDeliveries: 0
  }));

  return {
    id: generatePatientId(),
    firstName: data.patient.firstName,
    lastName: data.patient.lastName,
    dateOfBirth: data.patient.dateOfBirth,
    mrn: data.patient.mrn,
    lastVisit: new Date().toISOString().split('T')[0],
    riskLevel: data.patient.riskLevel,
    healthMetrics,
    wellbeingFactors: defaultWellbeingFactors,
    labResults: data.labResults,
    medications,
    allergies: [], // Allergies not available in EHR eventstream
    appleHealthData: generateDefaultAppleHealthData(),
    plans: defaultHealthPlan,
    medicalTimeline: {}, // Medical timeline not available in EHR eventstream
    historicalData: data.historicalData
  };
}

// Re-export types for backwards compatibility
export type { DashboardData as ParsedEhrData } from './parseXmlToJson';
